import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StatusCode } from "api/protocols";
import { openPrintWindow, valueToString } from "helpers/Helpers";
import { THIS_MONTH, THIS_YEAR } from "components/form/calendarField/helpers";
import { annualFiltersObj } from "hooks/useOrderFilters/useDropFilters";
import { Badge } from "components/badge/Badge";
import DropFilter from "components/dropdown/DropFilter";
import Page from "components/page/Page";
import "../facility/facility.scss";
import DataTable from "../../../../../components/dataTable/DataTable";
import axios, { CancelToken } from "axios";
import {
  GetPayout,
  GetExportPayouts,
  GetPrintPayouts,
  GetPrintSinglePayout,
} from "api/rpc/2024-04/facilityAdmin/facility/payout";
import { LocaleCurrency } from "helpers/Locale";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint } from "@fortawesome/pro-regular-svg-icons";

const TABLE_LIMIT = 25;

export default function Payouts() {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const payoutFilters = annualFiltersObj();
  const [filterState, setFilterState] = useState({
    month: THIS_MONTH,
    year: THIS_YEAR,
    offset: 0,
  });

  const [payoutState, setPayoutState] = useState({ payout: null });

  useEffect(() => {
    const source = axios.CancelToken.source();

    void loadPayouts(source.token);

    return () => {
      source.cancel();
    };
  }, [filterState.month, filterState.year, filterState.offset]);

  async function loadPayouts(token?: CancelToken) {
    console.log("----> API CALL", filterState); //debug

    if (payoutState.payout) {
      setPayoutState(prevState => ({ ...prevState, payout: null }));
    }

    const res = await GetPayout({ ...filterState, limit: TABLE_LIMIT }, token ? false : true, token);

    console.log(res);

    if (token && token.reason) {
      return;
    }

    if (res.status !== StatusCode.OK) {
      setPayoutState(prevState => ({ ...prevState, payout: [] }));

      return;
    }

    setPayoutState(prev => ({
      ...prev,
      payout: res.data,
    }));
  }

  async function exportPayouts() {
    const exportRes = await GetExportPayouts(filterState, true);

    if (exportRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error exporting payouts")));
      return;
    }

    const payoutExport = document.createElement("a");

    payoutExport.href = "data:text/csv;charset=utf-8," + encodeURI(exportRes.data as string);
    payoutExport.target = "_blank";
    payoutExport.download = `${"Payouts"}_${moment(new Date()).format("YYYY-MM-DD").toString()}`
      .replace(/ /g, "_")
      .toLowerCase()
      .concat(".csv");
    payoutExport.click();
  }

  function navigateToViewPayout(url: string) {
    history.push(url);
  }

  function handleApplyFilter(filterReturn: Record<string, any>[], type: string) {
    const tempReturn = filterReturn[0]; //works because of 'restrain' prop on dropfilter

    if (tempReturn === undefined) {
      setFilterState({ ...filterState, [type]: undefined });
      return;
    }

    setFilterState({ ...filterState, [type]: tempReturn.id });
  }

  function handleTableNavigation(direction: "prev" | "next") {
    switch (direction) {
      case "prev": {
        setFilterState(prev => ({ ...prev, offset: filterState.offset - TABLE_LIMIT }));
        return;
      }
      case "next": {
        setFilterState(prev => ({ ...prev, offset: filterState.offset + TABLE_LIMIT }));
        return;
      }
      default:
        return;
    }
  }

  async function handlePrintPayout() {
    const params = {
      // payout_id: payout_id,
      offset: filterState.offset,
      year: filterState.year,
      month: filterState.month,
    };
    const exportRes = await GetPrintPayouts(params, true);

    if (exportRes.status !== StatusCode.OK) {
      dispatch(showError("Error printing payouts"));
      return;
    }

    openPrintWindow(exportRes.data as string);
  }

  async function handlePrintSinglePayout(e: React.MouseEvent<SVGSVGElement, MouseEvent>, payout_id?: number) {
    e.stopPropagation();
    const params = {
      id: payout_id,
    };

    const exportRes = await GetPrintSinglePayout(params, true);

    if (exportRes.status !== StatusCode.OK) {
      dispatch(showError("Error printing payouts"));
      return;
    }

    window.open().document.write(exportRes.data as string);
  }

  const primaryAction = {
    content: t("Export Payouts"),
    action: () => exportPayouts(),
  };

  const secondaryActions = [
    {
      content: t("Print"),
      action: () => handlePrintPayout(),
    },
  ];

  return (
    <Page title={t("Payouts")} primaryAction={primaryAction} secondaryActions={secondaryActions}>
      <div className="flex flex-row flex-wrap gap-4 pb-4">
        <DropFilter
          key="months"
          title={t("Month")}
          filterData={payoutFilters.monthFilters("index")}
          filterLabelPropFromData="label"
          filterIdPropFromData="id"
          filterType="Checkbox"
          applyFilters={filterReturn => handleApplyFilter(filterReturn, "month")}
          defaultCheckboxes={[filterState.month]}
          restrain
          leftAligned
        />

        <DropFilter
          key="year"
          title={t("Year")}
          filterData={payoutFilters.yearFilters()}
          filterLabelPropFromData="label"
          filterIdPropFromData="id"
          filterType="Checkbox"
          applyFilters={filterReturn => handleApplyFilter(filterReturn, "year")}
          defaultCheckboxes={[filterState.year]}
          restrain
        />
      </div>

      <DataTable
        loading={!payoutState?.payout ? true : false}
        columns={[
          { label: t("Date") },
          { label: t("Status") },
          { label: t("Currency") },
          { label: t("Gross") },
          { label: t("Fee") },
          { label: t("Net") },
          { label: t("Carry Forward") },
          { label: t("Adjusted Net") },
          { label: t("Process Date") },
          { label: t("Authorization") },
          { label: "" },
        ]}
        footer={{
          tableLimit: TABLE_LIMIT,
          tableOffset: filterState.offset,
          disableNextOffset: !payoutState?.payout || !(payoutState?.payout?.length === TABLE_LIMIT),
          handleTableOffset: direction => handleTableNavigation(direction),
        }}
      >
        {payoutState.payout?.map((payout: any, index: any) => {
          return (
            <tr
              key={index}
              className="clickable"
              onClick={() => navigateToViewPayout("/admin/settings/payouts/" + String(payout.id))}
            >
              <td>{payout.statement_description}</td>
              <td>
                <Badge type="gray">{valueToString(payout.status as string, "capitalize")}</Badge>
              </td>
              <td>{valueToString(payout.currency as string, "uppercase")}</td>
              <td>
                <LocaleCurrency currency={payout.currency} amount={payout.gross} />
              </td>
              <td>
                <LocaleCurrency currency={payout.currency} amount={payout.fee} />
              </td>
              <td>
                <LocaleCurrency currency={payout.currency} amount={payout.net} />
              </td>
              <td>
                <LocaleCurrency currency={payout.currency} amount={payout.carry_forward_in} />
              </td>
              <td>
                <LocaleCurrency currency={payout.currency} amount={payout.adjusted_net} />
              </td>
              <td>{payout.process_date}</td>
              <td>{payout.authorization}</td>
              <td>
                {<FontAwesomeIcon icon={faPrint} onClick={e => handlePrintSinglePayout(e, payout.id as number)} />}
              </td>
            </tr>
          );
        })}
      </DataTable>
    </Page>
  );
}
